import { useMemo } from 'react';
import { Box, ToggleButton, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { TEditorBarButtonsProps, TToolbarButton } from '../types';

export const EditorBarButtons = ({
  articleMainContent,
  buttons,
  editor,
  enabledButtons,
  disabled,
  isMobileMenu = false,
  tooltipPlacement,
}: TEditorBarButtonsProps) => {
  const allButtonKeys = useMemo(() => buttons?.map((button) => button?.key), [buttons]);
  const isAllButtonsEnabled = enabledButtons === 'all';
  const isToolbarEnabled = !disabled && editor;

  // Render the buttons, handles nullish.
  return (
    <>
      {buttons.map((button: TToolbarButton) => {
        // Check if the button should be rendered.
        const shouldRenderButton: boolean | undefined =
          (isAllButtonsEnabled ||
            articleMainContent ||
            allButtonKeys.includes(button.key)) &&
          (button.additionalCheck === undefined || button.additionalCheck);

        // Ensure the button should be rendered.
        if (!shouldRenderButton) return null;

        const isDisabled = disabled || !isToolbarEnabled || button?.disabled;

        const classes = classNames(
          'toolbar-button',
          button?.key || '',
          button?.className !== undefined
            ? button.className
            : { 'Mui-selected': !disabled && editor?.isActive(button.key) },
        );

        // If the button has a component prop, render it with Container.
        if (button.component && !isMobileMenu) {
          return (
            <Box
              data-testid={`editor-bar-button-${button.key}`}
              key={button.key}
              className={classes}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <button.component
                editor={editor}
                disabled={isDisabled}
              />
            </Box>
          );
        }

        if (button.component && isMobileMenu) {
          return (
            <button.component
              data-testid={`editor-bar-button-${button.key}`}
              key={button.key}
              editor={editor}
              disabled={isDisabled}
            />
          );
        }

        // If the button has a title, onClick, and icon, render it with ToggleButton.
        if (button.title && button.onClick && button.icon) {
          return (
            <ToggleButton
              data-testid={`editor-bar-button-${button.key}`}
              key={button.key}
              value={button.key}
              aria-label={button.title}
              onClick={button.onClick}
              disabled={isDisabled}
              className={classes}
            >
              <Tooltip
                title={button.tooltipTitle}
                placement={tooltipPlacement}
                disableInteractive
                slotProps={{ popper: { keepMounted: true } }}
              >
                <button.icon />
              </Tooltip>
            </ToggleButton>
          );
        }

        // Default return.
        return null;
      })}
    </>
  );
};

export default EditorBarButtons;
